import styled, { useTheme } from 'styled-components';
import { SearchInputProps } from './search-input';
import {
  ArrowRightIcon,
  CaptionText,
  FlexColumn,
  FlexRow,
  SvgIcon,
} from '@make-software/cspr-design';
import { BodyText, Link } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import {
  getAccountSearchResults,
  SearchByNameResults,
} from '../../hooks/search-utils';
import { useCreateAccountsInfoGetter } from '../../hooks';
import {
  SearchDropdownContract,
  SearchDropdownItems,
} from './search-dropdown-items';
import Skeleton from 'react-loading-skeleton';

const DropdownContainer = styled(FlexColumn)<
  Pick<SearchInputProps, 'top' | 'width'>
>(({ theme, top, width }) =>
  theme.withMedia({
    width: width || '100%',
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    borderRadius: theme.borderRadius.base,
    position: 'absolute',
    top: top,
    zIndex: theme.zIndex.dropdown,
    boxShadow: theme.boxShadow.tooltip,
    padding: '16px 0px',
    marginTop: '8px',
  })
);

const DropdownGroupLabelContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    zIndex: theme.zIndex.dropdown,
    position: 'sticky',
    top: '0',
    width: '100%',
    padding: '0px 19px 5px',
    backgroundColor: theme.styleguideColors.backgroundPrimary,

    '&:not(:nth-child(1))': {
      marginTop: '14px',
    },
  })
);

const DropdownViewMore = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    background: theme.styleguideColors.backgroundPrimary,
    padding: '8px 16px',
    alignItems: 'center',
    marginTop: '8px',
    path: {
      fill: theme.styleguideColors.fillSecondaryBlueHover,
    },
  })
);

const DropdownItem = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    height: 80,
    padding: '8px 16px',
    alignItems: 'center',
    '&:not(:nth-child(1))': {
      marginTop: '4px',
    },

    '&:hover': {
      background: theme.styleguideColors.fillSecondaryBlueHover,
    },
  })
);

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentLightBlue,
  },
}));

const OverflowContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    maxHeight: 500,
    overflowY: 'auto',

    '&::-webkit-scrollbar': {
      width: '14px',
      height: '18px',
    },

    '&::-webkit-scrollbar-thumb': {
      height: '6px',
      border: '4px solid rgba(0, 0, 0, 0)',
      backgroundClip: 'padding-box',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      '-webkit-border-radius': '7px',
      '-webkit-box-shadow':
        'inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05)',
    },

    '&::-webkit-scrollbar-button': {
      display: 'none',
      width: '0',
      height: '0',
    },

    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
  })
);

const DropdownGroupLabel = ({ label }) => {
  return (
    <DropdownGroupLabelContainer>
      <CaptionText size={2} variation="darkGray" noWrap uppercase>
        {label}
      </CaptionText>
    </DropdownGroupLabelContainer>
  );
};

export const SearchDropdown = ({
  data,
  loading,
  onClickAllResults,
  onSelectItem,
}: {
  data: SearchByNameResults;
  loading?: boolean;
  onClickAllResults: () => void;
  onSelectItem: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const RESULT_LIMIT = 10;

  const contractPackages = data?.contractPackages?.slice(0, RESULT_LIMIT) || [];
  const centralizedAccounts =
    data?.centralizedAccounts?.data.slice(0, RESULT_LIMIT) || [];
  const verifiedAccounts = data?.verifiedAccounts?.slice(0, RESULT_LIMIT) || [];

  const accountHashesList = [
    ...verifiedAccounts.map((accountInfo) => accountInfo.account_hash),
    ...centralizedAccounts.map((accountInfo) => accountInfo.account_hash),
  ];

  const { getAccountInfoByHash, loading: accountsInfoLoading } =
    useCreateAccountsInfoGetter(null, accountHashesList);

  if (loading) {
    return (
      <DropdownContainer>
        {Array.from({ length: 3 }).map(() => (
          <DropdownItem justify={'space-between'}>
            <FlexRow gap={16}>
              <FlexColumn>
                <Skeleton
                  count={1}
                  height={'56px'}
                  width={'56px'}
                  baseColor={theme.styleguideColors.backgroundPrimary}
                  highlightColor={theme.styleguideColors.fillSecondary}
                />
              </FlexColumn>
              <FlexColumn>
                <Skeleton
                  count={1}
                  height={'24px'}
                  width={'12rem'}
                  baseColor={theme.styleguideColors.backgroundPrimary}
                  highlightColor={theme.styleguideColors.fillSecondary}
                />
                <Skeleton
                  count={1}
                  height={'24px'}
                  width={'8rem'}
                  baseColor={theme.styleguideColors.backgroundPrimary}
                  highlightColor={theme.styleguideColors.fillSecondary}
                />
              </FlexColumn>
            </FlexRow>
          </DropdownItem>
        ))}
      </DropdownContainer>
    );
  }

  const accounts = getAccountSearchResults(
    verifiedAccounts,
    centralizedAccounts,
    getAccountInfoByHash
  ).slice(0, RESULT_LIMIT);

  const renderAccounts = () => {
    if (accounts.length === 0) {
      return null;
    }

    const accountItems = [
      <DropdownGroupLabel key={`label-account`} label={t('Accounts')} />,
    ];

    const items = accounts.map((account, index) => {
      return (
        <DropdownItem key={`account-${account.accountHash}`}>
          <SearchDropdownItems
            key={`account-${index}`}
            account={account}
            onSelect={onSelectItem}
            accountInfoLoading={accountsInfoLoading}
          />
        </DropdownItem>
      );
    });

    items.unshift(...accountItems);

    return items;
  };

  const renderContracts = () => {
    if (contractPackages.length === 0) {
      return null;
    }

    const contractItems = [
      <DropdownGroupLabel key={`label-contracts`} label={t('Contracts')} />,
    ];

    const items = contractPackages.map((contractPackage, index) => {
      return (
        <DropdownItem
          key={`contract-package-${contractPackage.contract_package_hash}`}
        >
          <SearchDropdownContract
            contractName={contractPackage.name}
            onSelect={onSelectItem}
            logo={contractPackage.icon_url}
            websiteUrl={contractPackage.website_url}
            hash={contractPackage.contract_package_hash}
            isFeatured={contractPackage.is_featured}
            latestVersionContractTypeId={
              contractPackage.latest_version_contract_type_id
            }
            accountInfoLoading={accountsInfoLoading}
            key={`account-${index}`}
          />
        </DropdownItem>
      );
    });

    items.unshift(...contractItems);

    return items;
  };

  return (
    <DropdownContainer>
      <OverflowContainer>
        {renderContracts()}
        {renderAccounts()}
      </OverflowContainer>
      <DropdownViewMore>
        <BodyText size={3} scale={'sm'} onClick={onClickAllResults}>
          <Link color={'primaryBlue'}>
            <Trans t={t}>See all results</Trans>
          </Link>
          <StyledSvgIcon src={ArrowRightIcon} alt={'Arrow icon'} marginLeft />
        </BodyText>
      </DropdownViewMore>
    </DropdownContainer>
  );
};
